import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Global Notification Context
 * Tillhandahåller toast-meddelanden och modals för hela applikationen
 */
const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications måste användas inom NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  // Toast state
  const [toast, setToast] = useState(null);
  
  // Modal state
  const [modal, setModal] = useState(null);

  /**
   * Visa ett toast-meddelande
   * @param {string} message - Meddelandet att visa
   * @param {string} type - 'success' | 'error' | 'info' | 'warning'
   * @param {number} duration - Hur länge toasten ska visas (ms), 0 = ingen auto-hide
   */
  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    setToast({ message, type });
    
    if (duration > 0) {
      setTimeout(() => {
        setToast(null);
      }, duration);
    }
  }, []);

  /**
   * Dölj aktuell toast
   */
  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  /**
   * Visa en success modal
   * @param {string} title - Modalens titel
   * @param {string} message - Modalens meddelande
   * @param {Function|Object} onCloseOrOptions - Callback när modal stängs, eller options-objekt
   * Options: { onClose, actions: [{ label, onClick, primary }] }
   */
  const showSuccess = useCallback((title, message, onCloseOrOptions) => {
    const isOptions = typeof onCloseOrOptions === 'object' && onCloseOrOptions !== null;
    const onClose = isOptions ? onCloseOrOptions.onClose : onCloseOrOptions;
    const actions = isOptions ? onCloseOrOptions.actions : null;

    setModal({
      type: 'success',
      title,
      message,
      actions,
      onClose: () => {
        setModal(null);
        if (onClose) onClose();
      }
    });
  }, []);

  /**
   * Visa en error modal
   * @param {string} title - Modalens titel
   * @param {string} message - Modalens meddelande
   * @param {Function} onClose - Callback när modal stängs
   */
  const showError = useCallback((title, message, onClose) => {
    setModal({
      type: 'error',
      title,
      message,
      onClose: () => {
        setModal(null);
        if (onClose) onClose();
      }
    });
  }, []);

  /**
   * Visa en info modal
   * @param {string} title - Modalens titel
   * @param {string} message - Modalens meddelande
   * @param {Function} onClose - Callback när modal stängs
   */
  const showInfo = useCallback((title, message, onClose) => {
    setModal({
      type: 'info',
      title,
      message,
      onClose: () => {
        setModal(null);
        if (onClose) onClose();
      }
    });
  }, []);

  /**
   * Visa en confirm dialog
   * @param {string} title - Dialogens titel
   * @param {string} message - Dialogens meddelande
   * @param {Function} onConfirm - Callback när användaren bekräftar
   * @param {Function} onCancel - Callback när användaren avbryter
   */
  const showConfirm = useCallback((title, message, onConfirm, onCancel) => {
    return new Promise((resolve) => {
      setModal({
        type: 'confirm',
        title,
        message,
        onConfirm: () => {
          setModal(null);
          if (onConfirm) onConfirm();
          resolve(true);
        },
        onCancel: () => {
          setModal(null);
          if (onCancel) onCancel();
          resolve(false);
        }
      });
    });
  }, []);

  /**
   * Dölj aktuell modal
   */
  const hideModal = useCallback(() => {
    setModal(null);
  }, []);

  const value = {
    // Toast API
    toast,
    showToast,
    hideToast,
    
    // Modal API
    modal,
    showSuccess,
    showError,
    showInfo,
    showConfirm,
    hideModal,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
